import type { TenantConfig } from '../types/tenant-config'
import { buildLocaleUrl } from './tenant-url'

/**
 * Language display names keyed by locale language code.
 * Used to show human-readable language names in the language bar.
 */
const LANGUAGE_DISPLAY_NAMES: Record<string, string> = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  nl: 'Nederlands',
  it: 'Italiano',
  es: 'Español',
  pt: 'Português',
  da: 'Dansk',
  sv: 'Svenska',
  nb: 'Norsk',
  fi: 'Suomi',
}

/**
 * Localized language selection titles (the prompt shown above language options).
 */
const LANGUAGE_SELECTION_TITLES: Record<string, string> = {
  en: 'Please select your preferred language:',
  fr: 'Veuillez sélectionner la langue de votre choix:',
  de: 'Bitte wählen Sie Ihre Sprache:',
  nl: 'Selecteer je voorkeurstaal:',
  it: 'Scegli la tua lingua preferita:',
  es: 'Seleccione su idioma preferido:',
  pt: 'Selecione o idioma de sua preferência:',
  da: 'Vælg venligst dit foretrukne sprog:',
  sv: 'Välj ditt föredragna språk:',
  nb: 'Velg ditt foretrukne språk:',
  fi: 'Valitse haluamasi kieli:',
}

export interface LanguageOption {
  url: string
  text: string
  title: string
  localeCode: string
}

export interface LanguageBarCountryData {
  alternateLanguages: LanguageOption[]
}

/**
 * Derives language bar data from the tenant configuration.
 * Only includes countries/sites that have multiple supported locales.
 *
 * Output shape is compatible with the existing `languageBarEnabledCountries` constant:
 * Record<countryCode, { alternateLanguages: LanguageOption[] }>
 *
 * @param tenantConfig - The resolved tenant configuration
 * @returns Record of country codes to their language options (only multi-locale countries)
 */
export function deriveLanguageBarData(tenantConfig: TenantConfig): Record<string, LanguageBarCountryData> {
  const result: Record<string, LanguageBarCountryData> = {}

  for (const site of tenantConfig.sites) {
    // Only include sites with multiple supported locales
    if (site.locales.supported.length <= 1) continue

    const countryCode = site.countryCode

    // Build language options for each supported locale
    const alternateLanguages: LanguageOption[] = site.locales.supported.map((locale) => {
      const langCode = locale.split('-')[0]?.toLowerCase() ?? ''
      return {
        url: buildLocaleUrl(site, locale),
        text: LANGUAGE_DISPLAY_NAMES[langCode] ?? langCode.charAt(0).toUpperCase() + langCode.slice(1),
        title: LANGUAGE_SELECTION_TITLES[langCode] ?? LANGUAGE_SELECTION_TITLES['en']!,
        localeCode: locale,
      }
    })

    result[countryCode] = { alternateLanguages }
  }

  return result
}
